import { beatDuration } from './clock'

/**
 * Transporte (§5.3): detenido, cuenta de entrada, sonando y pausado. Es un valor inmutable y cada
 * operación devuelve uno nuevo; la posición no se guarda en ningún sitio, se calcula a partir del
 * reloj de audio cuando se pide (regla 2, ADR 0002).
 *
 * Mientras suena, la posición sale de un «tramo»: el instante del reloj en que suena un pulso de
 * la canción y el tempo. Cambiar el tempo, saltar o poner un bucle abre un tramo nuevo que empieza
 * en el siguiente pulso del anterior: el pulso no se interrumpe y lo que ya estaba programado
 * hasta ese instante sigue valiendo. Empezar o reanudar abre un tramo con cuenta de entrada.
 */
export interface Loop {
  /** Pulsos enteros de la canción: el bucle va de `start` hasta justo antes de `end`. */
  start: number
  end: number
}

export interface Run {
  /** Instante del reloj de audio en que suena el pulso `beat` de la canción. */
  time: number
  /** Pulso de la canción (entero) en `time`. */
  beat: number
  bpm: number
  /** Clics de cuenta de entrada antes de `time`. */
  countIn: number
  loop: Loop | null
}

interface Settings {
  bpm: number
  loop: Loop | null
}

export type TransportState =
  | (Settings & { status: 'stopped'; cursor: number })
  | (Settings & { status: 'paused'; cursor: number; returnTo: number })
  | (Settings & { status: 'running'; run: Run; previous: Run | null; returnTo: number })

export type Phase = 'stopped' | 'paused' | 'countIn' | 'playing'

export interface Position {
  phase: Phase
  /** Pulso de la canción, con decimales. Durante la cuenta de entrada, el pulso en que se entra. */
  beat: number
  /** Clics de cuenta que faltan (4, 3, 2, 1) durante la cuenta de entrada; si no, 0. */
  countInLeft: number
}

/** Segundos entre pulsar «Tocar» y el primer clic: deja tiempo a programarlo. */
export const START_LEAD = 0.1
/** Un cambio no se aplica a un pulso que va a sonar antes de este margen: ya está programado. */
export const CHANGE_MARGIN = 0.03

const EPSILON = 1e-9

export function createTransport(bpm: number): TransportState {
  return { status: 'stopped', cursor: 0, bpm, loop: null }
}

/** El pulso de la canción que corresponde a un pulso «en bruto» de un tramo, con el bucle. */
export function songBeat(run: Run, raw: number): number {
  const { loop } = run
  // Con margen: un clic que cae justo en el final del bucle ya es su inicio, aunque la coma
  // flotante diga 47,9999999.
  if (!loop || run.beat >= loop.end || raw < loop.end - EPSILON) return raw
  const length = loop.end - loop.start
  const wrapped = Math.max(0, raw - loop.end) % length
  return loop.start + (length - wrapped < EPSILON ? 0 : wrapped)
}

/** Primer instante del tramo, contando su cuenta de entrada. */
function runOpening(run: Run): number {
  return run.time - run.countIn * beatDuration(run.bpm)
}

function activeRun(state: TransportState & { status: 'running' }, time: number): Run {
  return state.previous && time < runOpening(state.run) ? state.previous : state.run
}

function positionInRun(run: Run, time: number): Position {
  if (time < run.time) {
    const left = Math.ceil((run.time - time) / beatDuration(run.bpm) - EPSILON)
    return {
      phase: 'countIn',
      beat: run.beat,
      countInLeft: Math.min(Math.max(left, run.countIn > 0 ? 1 : 0), run.countIn),
    }
  }
  const raw = run.beat + (time - run.time) / beatDuration(run.bpm)
  return { phase: 'playing', beat: songBeat(run, raw), countInLeft: 0 }
}

export function positionAt(state: TransportState, time: number): Position {
  if (state.status !== 'running') {
    return { phase: state.status, beat: state.cursor, countInLeft: 0 }
  }
  return positionInRun(activeRun(state, time), time)
}

export interface PlayOptions {
  /** Pulso desde el que empezar. Por defecto, donde está el cursor. */
  from?: number
  /** Pulsos de cuenta de entrada. */
  countIn: number
}

/** Empieza (o reanuda) tras la cuenta de entrada. Siempre entra en un pulso entero. */
export function play(state: TransportState, now: number, options: PlayOptions): TransportState {
  if (state.status === 'running') return state
  const start = Math.max(0, Math.floor((options.from ?? state.cursor) + EPSILON))
  const countIn = Math.max(0, Math.round(options.countIn))
  const run: Run = {
    time: now + START_LEAD + countIn * beatDuration(state.bpm),
    beat: start,
    bpm: state.bpm,
    countIn,
    loop: state.loop,
  }
  // Al reanudar, «Parar» sigue volviendo a donde se empezó.
  const returnTo = state.status === 'paused' ? state.returnTo : start
  return { status: 'running', bpm: state.bpm, loop: state.loop, run, previous: null, returnTo }
}

/** Pausa en el pulso que se está oyendo en `heard`. */
export function pause(state: TransportState, heard: number): TransportState {
  if (state.status !== 'running') return state
  const cursor = Math.max(0, positionAt(state, heard).beat)
  return { status: 'paused', cursor, bpm: state.bpm, loop: state.loop, returnTo: state.returnTo }
}

/** Para y vuelve al pulso desde el que se empezó. */
export function stop(state: TransportState): TransportState {
  if (state.status === 'stopped') return state
  return { status: 'stopped', cursor: state.returnTo, bpm: state.bpm, loop: state.loop }
}

export interface Tick {
  time: number
  /** Índice del clic dentro de su tramo: negativo en la cuenta de entrada. */
  index: number
  /** Pulso de la canción en ese clic (en la cuenta de entrada, el pulso en que se entra). */
  beat: number
  countIn: boolean
}

/** El clic `index` de un tramo. */
export function tickOf(run: Run, index: number): Tick {
  const time = run.time + index * beatDuration(run.bpm)
  if (index < 0) return { time, index, beat: run.beat, countIn: true }
  return { time, index, beat: songBeat(run, run.beat + index), countIn: false }
}

/** Primer clic del tramo que suena en `time` o después. */
function firstTickFrom(run: Run, time: number): Tick {
  const index = Math.max(
    -run.countIn,
    Math.ceil((time - run.time) / beatDuration(run.bpm) - EPSILON),
  )
  return tickOf(run, index)
}

/** El siguiente clic que todavía se puede cambiar. */
function nextChangeableTick(state: TransportState & { status: 'running' }, now: number): Tick {
  const threshold = now + CHANGE_MARGIN
  const { previous, run } = state
  if (previous && threshold < runOpening(run)) {
    const tick = firstTickFrom(previous, threshold)
    if (tick.time < runOpening(run)) return tick
  }
  return firstTickFrom(run, threshold)
}

/**
 * Abre un tramo nuevo en el siguiente clic. Si ese clic era de la cuenta de entrada, la cuenta
 * sigue con los clics que le faltaban, ya con el tempo nuevo.
 */
function changeAtNextTick(
  state: TransportState & { status: 'running' },
  now: number,
  change: { bpm?: number; loop?: Loop | null; beat?: number },
): TransportState {
  const tick = nextChangeableTick(state, now)
  const bpm = change.bpm ?? state.bpm
  const loop = change.loop === undefined ? state.loop : change.loop
  const countIn = tick.countIn ? -tick.index : 0
  const run: Run = {
    time: tick.time + countIn * beatDuration(bpm),
    beat: change.beat ?? tick.beat,
    bpm,
    countIn,
    loop,
  }
  // Hasta el clic del cambio manda el tramo que lo contenía. Si había otro cambio pendiente que
  // todavía no ha empezado a sonar, este lo sustituye.
  const governing =
    state.previous && tick.time <= runOpening(state.run) ? state.previous : state.run
  return { ...state, bpm, loop, run, previous: governing }
}

/** Cambia el tempo conservando la posición en pulsos. */
export function setBpm(state: TransportState, now: number, bpm: number): TransportState {
  if (bpm === state.bpm) return state
  if (state.status !== 'running') return { ...state, bpm }
  return changeAtNextTick(state, now, { bpm })
}

/**
 * Salta a un pulso. Sonando, el pulso sigue sin cortarse: el salto ocurre en el siguiente clic.
 * Parado o en pausa, solo mueve el cursor.
 */
export function seek(state: TransportState, now: number, beat: number): TransportState {
  const target = Math.max(0, beat)
  if (state.status === 'stopped') return { ...state, cursor: target }
  if (state.status === 'paused') return { ...state, cursor: target, returnTo: target }
  return changeAtNextTick(state, now, { beat: Math.floor(target + EPSILON) })
}

/** Pone o quita el bucle. Los límites se redondean a pulsos enteros para que el pulso no se descuadre. */
export function setLoop(state: TransportState, now: number, loop: Loop | null): TransportState {
  const normalized = loop ? normalizeLoop(loop) : null
  if (sameLoop(normalized, state.loop)) return state
  if (state.status !== 'running') return { ...state, loop: normalized }
  return changeAtNextTick(state, now, { loop: normalized })
}

export function normalizeLoop(loop: Loop): Loop | null {
  const start = Math.max(0, Math.round(loop.start))
  const end = Math.round(loop.end)
  return end > start ? { start, end } : null
}

function sameLoop(a: Loop | null, b: Loop | null): boolean {
  return a === b || (a !== null && b !== null && a.start === b.start && a.end === b.end)
}

/** Los tramos que todavía pueden sonar, con el instante en que deja de mandar cada uno. */
export function activeRuns(state: TransportState): { run: Run; until: number }[] {
  if (state.status !== 'running') return []
  const runs: { run: Run; until: number }[] = []
  if (state.previous) runs.push({ run: state.previous, until: runOpening(state.run) })
  runs.push({ run: state.run, until: Number.POSITIVE_INFINITY })
  return runs
}

/** ¿Llega a sonar el final de la canción? Con un bucle que lo envuelve, nunca. */
export function reachesEnd(state: TransportState, time: number, total: number): boolean {
  const position = positionAt(state, time)
  return position.phase === 'playing' && position.beat >= total - EPSILON
}
